/**
 * AuthBridge - Bridge between React (Kinde SDK hooks) and non-React code
 *
 * Ensures Kinde operations happen within proper React context.
 *
 * NOTE: the Kinde SDK does NOT refresh tokens inside getAccessToken() — it is
 * a raw read of the in-memory token, and the SDK's background refresh stops
 * silently after a single failed attempt (e.g. after laptop sleep). API calls
 * must go through getValidAccessToken(), which validates expiry and refreshes
 * directly against Kinde when needed.
 */
import { jwtDecode } from 'jwt-decode'

import {
  setKindeSession,
  teardownLocalSession,
} from '../features/auth/lib/authSession.js'
import { getKindeAudience } from '../features/auth/lib/kindeOrg.js'

// Refresh when the token expires within this buffer. Must stay well below
// the SDK's own ~10s-before-expiry refresher's window: a large buffer (was
// 300s) made this bridge refresh tokens the SDK still considered fresh, so
// every token lifetime paid two rotations and doubled the race surface on
// the single-use rotating refresh token.
const TOKEN_EXPIRY_BUFFER_SECONDS = 45
// A hung /oauth2/token call must not hang API requests indefinitely
const REFRESH_FETCH_TIMEOUT_MS = 15000
// Wait 250-500ms before the single retry after invalid_grant: Kinde rotates
// refresh tokens with only a short grace overlap, so a concurrent refresher
// (SDK timer, tab focus, another tab) may have won the race — the wait gives
// the winner time to persist the rotated token before we re-read storage
const REFRESH_RETRY_BASE_DELAY_MS = 250
const REFRESH_RETRY_JITTER_MS = 250
// verifySession() result is reused within this window so a persistent backend
// outage (polling keeps failing) doesn't hammer the Kinde token endpoint
const VERIFY_SESSION_COOLDOWN_MS = 30000

class AuthBridge {
  constructor() {
    this.kindeAuth = null
    this.pendingLogouts = new Set()
    this.debugPrefix = '[AUTH-BRIDGE]'
    this.pendingRefresh = null // Promise cache for token refresh deduplication
    this.lastSessionVerify = null // { result, at } — verifySession() cooldown cache
  }

  /**
   * Set the Kinde auth instance from React hooks
   * Called from AuthGate with access to useKindeAuth()
   */
  setKindeAuth(kindeAuth) {
    this.kindeAuth = kindeAuth

    // Process any pending logout requests that came in before hooks were available
    if (this.pendingLogouts.size > 0) {
      this._processPendingLogouts()
    }
  }

  /**
   * Execute logout with proper React hook context
   * Ensures Kinde server session is properly cleared
   */
  async logout(reason = 'manual') {
    if (this.kindeAuth?.logout) {
      try {
        await this.kindeAuth.logout()
        return { success: true, method: 'hook' }
      } catch (error) {
        console.error(`${this.debugPrefix} ❌ Kinde logout failed:`, error)
        return { success: false, error: error.message, method: 'hook' }
      }
    } else {
      this.pendingLogouts.add({
        reason,
        timestamp: Date.now(),
        id: Math.random().toString(36).substr(2, 9),
      })
      return { success: false, method: 'queued' }
    }
  }

  /**
   * Get the access token as cached by the Kinde SDK.
   * This is a raw storage read — the token may be expired.
   */
  async getAccessToken() {
    if (!this.kindeAuth?.getAccessToken) {
      return null
    }

    try {
      const token = await this.kindeAuth.getAccessToken()
      return token
    } catch (error) {
      console.error(`${this.debugPrefix} Failed to get access token:`, error)
      return null
    }
  }

  /**
   * Check if a token is expired or close enough to expiry to need a refresh
   */
  isTokenExpired(token) {
    if (!token) return true

    try {
      const decoded = jwtDecode(token)
      const now = Math.floor(Date.now() / 1000)
      return decoded.exp < now + TOKEN_EXPIRY_BUFFER_SECONDS
    } catch (error) {
      console.warn(`${this.debugPrefix} Failed to decode token:`, error)
      return true // Treat invalid tokens as expired
    }
  }

  /**
   * Get a token that is valid for at least the expiry buffer, refreshing
   * against Kinde when both cached copies are stale.
   *
   * Returns null when there is no session at all (unauthenticated requests).
   * Throws when a refresh was needed and failed — on a definitively dead
   * refresh token the local session is torn down (no Kinde logout);
   * on transient failures (network/timeout) the session is kept so the
   * next request can retry.
   */
  async getValidAccessToken() {
    // 1. Token from the SDK's in-memory storage
    const sdkToken = await this.getAccessToken()
    if (sdkToken && !this.isTokenExpired(sdkToken)) {
      setKindeSession(sdkToken) // keep other code paths in sync
      return sdkToken
    }

    // 2. Token from a previous direct refresh — the SDK's in-memory copy
    //    stays stale after a direct refresh, so this is the source of truth
    const storedToken = this._getStoredKindeToken()
    if (storedToken && !this.isTokenExpired(storedToken)) {
      return storedToken
    }

    // No session at all (never logged in / fully logged out) — let the
    // request go out unauthenticated rather than forcing a refresh cycle
    if (!sdkToken && !storedToken && !localStorage.getItem('refreshToken0')) {
      return null
    }

    // 3. Both copies stale — refresh directly against Kinde
    const result = await this.directRefreshToken()

    if (result.success && result.accessToken) {
      setKindeSession(result.accessToken)
      return result.accessToken
    }

    if (result.requiresReauth) {
      // Local teardown only — never Kinde /logout from an automatic path
      // (it would revoke the SSO session and cascade to every other tab)
      teardownLocalSession('token-refresh-failed')
      throw new Error('Session expired - please log in again')
    }

    throw new Error(`Token refresh failed: ${result.error}`)
  }

  /**
   * Probe whether the Kinde session is still alive via a forced refresh.
   * Used by apiClient before treating a backend auth error as a real logout:
   * the backend returns TOKEN_DATA_ERROR both for genuinely dead sessions
   * and for transient Kinde/DB hiccups, and only a refresh can tell them apart.
   *
   * @returns {Promise<'alive'|'dead'|'unknown'>} 'unknown' = transient failure
   */
  async verifySession() {
    if (
      this.lastSessionVerify &&
      Date.now() - this.lastSessionVerify.at < VERIFY_SESSION_COOLDOWN_MS
    ) {
      return this.lastSessionVerify.result
    }

    const refresh = await this.directRefreshToken()

    let result
    if (refresh.success && refresh.accessToken) {
      setKindeSession(refresh.accessToken) // next request recovers immediately
      result = 'alive'
    } else if (refresh.requiresReauth) {
      result = 'dead'
    } else {
      result = 'unknown'
    }

    this.lastSessionVerify = { result, at: Date.now() }
    return result
  }

  /**
   * Directly refresh tokens via Kinde OAuth2 endpoint
   * Bypasses SDK to get truly fresh tokens from server
   * Uses promise caching to prevent concurrent refresh requests
   * @returns {Promise<{success: boolean, accessToken?: string, refreshToken?: string, idToken?: string, error?: string, requiresReauth?: boolean}>}
   */
  async directRefreshToken() {
    // If a refresh is already in progress, return the same promise
    if (this.pendingRefresh) {
      return this.pendingRefresh
    }

    this.pendingRefresh = this._performTokenRefreshWithRetry()

    try {
      return await this.pendingRefresh
    } finally {
      // Clear the cached promise when done (success or failure)
      this.pendingRefresh = null
    }
  }

  /**
   * One refresh attempt, retried once when Kinde rejects the token with
   * invalid_grant: a rejected token does not prove the session is dead — a
   * concurrent refresher (SDK timer, tab-focus refresh, another tab) may have
   * rotated it out from under this call. The retry re-reads storage after a
   * jittered wait to pick up the winner's rotated token; only when the retry
   * also fails is the session treated as dead. request_unauthorized (revoked
   * by an explicit logout) is deterministic and is not retried.
   * @private
   */
  async _performTokenRefreshWithRetry() {
    const firstAttempt = await this._performTokenRefresh()
    if (
      !firstAttempt.requiresReauth ||
      firstAttempt.errorCode !== 'invalid_grant'
    ) {
      return firstAttempt
    }

    await new Promise((resolve) =>
      setTimeout(
        resolve,
        REFRESH_RETRY_BASE_DELAY_MS + Math.random() * REFRESH_RETRY_JITTER_MS,
      ),
    )
    return this._performTokenRefresh()
  }

  /**
   * Internal method that performs the actual token refresh
   * @private
   */
  async _performTokenRefresh() {
    try {
      const currentRefreshToken = this._readRefreshToken()

      if (!currentRefreshToken) {
        return {
          success: false,
          error: 'No refresh token available - re-authentication required',
          requiresReauth: true,
        }
      }

      // Note: REACT_APP_KINDE_DOMAIN already includes https:// protocol
      const tokenEndpoint = `${process.env.REACT_APP_KINDE_DOMAIN}/oauth2/token`
      // audience keeps the API `aud` claim on refreshed tokens (c0f144de)
      const audience = getKindeAudience()
      const requestBody = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: process.env.REACT_APP_KINDE_CLIENT_ID,
        refresh_token: currentRefreshToken,
        ...(audience && { audience }),
      })

      const abortController = new AbortController()
      const abortTimer = setTimeout(
        () => abortController.abort(),
        REFRESH_FETCH_TIMEOUT_MS,
      )

      let response
      try {
        response = await fetch(tokenEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: requestBody.toString(),
          signal: abortController.signal,
        })
      } finally {
        clearTimeout(abortTimer)
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error(
          `${this.debugPrefix} ❌ Token refresh failed:`,
          errorData.error,
        )

        // invalid_grant = expired/invalid token; request_unauthorized = token
        // revoked (Kinde returns it after a /logout ended the session) — both
        // need re-authentication, but invalid_grant may be a lost rotation
        // race, so the caller retries it once (errorCode drives that)
        if (
          errorData.error === 'invalid_grant' ||
          errorData.error === 'request_unauthorized'
        ) {
          return {
            success: false,
            error: 'Refresh token rejected - re-authentication required',
            errorCode: errorData.error,
            requiresReauth: true,
          }
        }

        return {
          success: false,
          error:
            errorData.error_description ||
            errorData.error ||
            `HTTP ${response.status}`,
        }
      }

      const tokenData = await response.json()

      // Update localStorage with new refresh token (critical for rotation)
      if (tokenData.refresh_token) {
        this._writeRefreshToken(tokenData.refresh_token)
      }

      return {
        success: true,
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token,
        idToken: tokenData.id_token,
        expiresIn: tokenData.expires_in,
      }
    } catch (error) {
      console.error(`${this.debugPrefix} ❌ Direct token refresh error:`, error)
      return {
        success: false,
        error: error.message,
      }
    }
  }

  /**
   * The SDK chunks a long refresh token across refreshToken0..N (2000-char
   * pieces) and reads it back by concatenating until the first missing
   * chunk. Reading only chunk 0 would silently truncate a chunked token and
   * turn it into an invalid_grant logout.
   * @private
   */
  _readRefreshToken() {
    let token = ''
    for (let i = 0; ; i++) {
      const chunk = localStorage.getItem(`refreshToken${i}`)
      if (chunk === null) break
      token += chunk
    }
    return token || null
  }

  /**
   * Persist the rotated refresh token to chunk 0 and drop any stale higher
   * chunks, so an SDK read (which concatenates 0..N) can't splice a previous
   * token's tail onto the new one. Writing the whole token to chunk 0 is
   * compatible with SDK reads regardless of length.
   * @private
   */
  _writeRefreshToken(token) {
    localStorage.setItem('refreshToken0', token)
    for (let i = 1; localStorage.getItem(`refreshToken${i}`) !== null; i++) {
      localStorage.removeItem(`refreshToken${i}`)
    }
  }

  /**
   * Read the persisted token copy, clearing it if corrupted
   * @private
   */
  _getStoredKindeToken() {
    try {
      return JSON.parse(localStorage.getItem('kindeToken') || 'null')
    } catch (error) {
      console.warn(
        `${this.debugPrefix} Corrupted stored token, clearing:`,
        error,
      )
      localStorage.removeItem('kindeToken')
      return null
    }
  }

  /**
   * Process any logout requests that were queued before hooks became available
   * @private
   */
  async _processPendingLogouts() {
    const logouts = Array.from(this.pendingLogouts)
    this.pendingLogouts.clear()

    for (const logoutRequest of logouts) {
      console.log(
        `${this.debugPrefix} Processing pending logout:`,
        logoutRequest,
      )
      await this.logout(`pending-${logoutRequest.reason}`)
    }
  }
}

// Export singleton instance
export const authBridge = new AuthBridge()

// Also export class for testing
export { AuthBridge }
